import type { CallbackState } from "@/sdk";
import type { SummaryResolution } from "@/shared-types";

export const selectProgramName = (
    useCustomerAgreementProgramName: boolean,
    wasClaimFiled: boolean,
    partnerConfigProgramName: string | undefined,
    customerAgreementProgramName: string | undefined,
    ignoreProgram: boolean,
    excludedPrograms: string[],
): string | undefined => {
    const programName = useCustomerAgreementProgramName ? customerAgreementProgramName : partnerConfigProgramName;
    const isExcludedProgram = excludedPrograms.some(
        (excludedProgram) => excludedProgram.toLowerCase() === programName?.toLowerCase(),
    );

    if (!programName || isExcludedProgram) {
        return ignoreProgram ? "NA" : undefined;
    }
    return wasClaimFiled ? `${programName}-retail` : programName;
};

/* Conditions for transfer to survey:
    1. Does not have a callback scheduled
    2. Does not have an active conference
    3. Customer has not already left after conference
    4. Resolution status is eligible for survey (controlled by feature flags)
*/
export const shouldTransferToSurvey = ({
    customerLeftTheConference,
    callbackState,
    isConference,
    conferencePanelOpen,
    resolutionStatus,
    surveyableResolutionStatuses,
}: {
    customerLeftTheConference: boolean;
    callbackState: CallbackState | null;
    isConference: boolean;
    conferencePanelOpen: boolean;
    resolutionStatus: SummaryResolution;
    surveyableResolutionStatuses: SummaryResolution[];
}): boolean => {
    const hasCallback = !!callbackState;
    const activeConference = isConference && conferencePanelOpen;

    const isResolutionSurveyable = surveyableResolutionStatuses.includes(resolutionStatus);

    return !(hasCallback || activeConference || customerLeftTheConference) && isResolutionSurveyable;
};

export const shouldSendSurveyData = ({
    customerLeftTheConference,
    callbackState,
    isConference,
    conferencePanelOpen,
}: {
    customerLeftTheConference: boolean;
    callbackState: CallbackState | null;
    isConference: boolean;
    conferencePanelOpen: boolean;
}): boolean => {
    const hasCallback = !!callbackState;
    const activeConference = isConference && conferencePanelOpen;

    return !hasCallback && !activeConference && !customerLeftTheConference;
};

export const buildAutomaticSurveyData = ({
    shouldShowResolutionMenu,
    resolutionStatus,
    isClaimEligible,
    claimFiled,
}: {
    shouldShowResolutionMenu: boolean;
    resolutionStatus: SummaryResolution;
    isClaimEligible: boolean;
    claimFiled: boolean;
}): Record<string, any> => {
    return {
        ...(shouldShowResolutionMenu && { resolution: resolutionStatus }),
        ...(isClaimEligible && { claimFiled }),
    };
};
